import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';

// Navigation items
const navItems = [
  { to: '/dashboard', label: 'Home', icon: '🏠' },
  { to: '/practice', label: 'Practice', icon: '📚' },
  { to: '/vet', label: 'Vet', icon: '🏥' },
  { to: '/store', label: 'Happy Store', icon: '🛒' },
];

function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-pink via-pastel-purple to-pastel-blue">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 shadow-lg backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
          >
            <span className="text-3xl">🐕</span>
            <span className="font-display text-2xl font-bold text-primary-600">StudyTail</span>
          </motion.div>

          {/* Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-2xl px-4 py-2 font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User info & logout */}
          <div className="flex items-center gap-4">
            {/* Paw Points display */}
            <div className="hidden items-center gap-2 rounded-full bg-primary-100 px-4 py-2 sm:flex">
              <span className="text-lg">🐾</span>
              <span className="font-semibold text-primary-700">
                {user?.totalPawPoints || 0} PP
              </span>
            </div>

            {/* User menu */}
            <div className="flex items-center gap-2">
              <span className="hidden text-sm text-gray-600 sm:block">
                {user?.displayName || user?.username || user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="rounded-xl bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Mobile navigation */}
        <nav className="flex justify-around border-t bg-white/50 px-2 py-2 md:hidden">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex flex-col items-center rounded-xl px-3 py-2 text-xs font-medium transition-all ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <span className="text-xl">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t bg-white/50 py-4">
        <div className="mx-auto max-w-7xl px-4 text-center text-sm text-gray-500">
          <p>Made with 💜 for study success</p>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
